import { useEffect, useState } from "react";
import { Button, Modal, QuantityStepper, Typography } from "@/components/atoms";
import { ClientBookingDataExtra } from "@/redux/apis/clientBooking/types";
import { ClientBookingExtra } from "@/redux/slices/clientBooking/types";
import { formatCurrency } from "@/utils";

/**
 * An extra's `amount` is in CENTS — the v3 job-data entity documents `cost` as "Cost of this extra
 * (in cents)" and the transform copies it through untouched. So this converts, unlike the summary's
 * `fmt`, which takes quote values already in dollars. Getting this wrong shows a $25 seat as $2,500.
 */
const price = (cents: number) => formatCurrency(cents, 2, "AUD");

/**
 * Upper bound per extra. Neither the design nor the backend caps this, but a stuck "+" shouldn't be
 * able to price forty baby seats onto a booking — and an operator with a genuine need for more can
 * still be asked for it.
 */
const MAX_QUANTITY = 10;

interface AddExtrasModalProps {
  open: boolean;
  /** The operator's bookable extras, in reference-data order. */
  options: ClientBookingDataExtra[];
  /** The current selection, so reopening the modal to edit shows what's already on the booking. */
  selected: ClientBookingExtra[];
  onClose: () => void;
  onConfirm: (extras: ClientBookingExtra[]) => void;
}

/**
 * Picks extras and how many of each. Quantities live here as a local `{ [extra_id]: count }` draft
 * and only reach the booking on "Add Extras" — Cancel and a re-open both discard them, so a client
 * can experiment in the modal without disturbing a priced booking behind it.
 */
export const AddExtrasModal = ({ open, options, selected, onClose, onConfirm }: AddExtrasModalProps) => {
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  // Reseed from the committed selection each time the modal opens, so Edit shows the booking's real
  // state and Cancel genuinely reverts.
  useEffect(() => {
    if (!open) return;
    setQuantities(Object.fromEntries(selected.map((extra) => [extra.extra_id, Math.max(1, extra.quantity ?? 1)])));
  }, [open, selected]);

  const setQuantity = (extraId: string, next: number) =>
    setQuantities((current) => {
      if (next < 1) {
        const { [extraId]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [extraId]: Math.min(next, MAX_QUANTITY) };
    });

  const chosen = options.filter((option) => quantities[option.extra_id]);
  const itemCount = chosen.reduce((total, option) => total + quantities[option.extra_id], 0);
  const total = chosen.reduce((sum, option) => sum + (option.amount ?? 0) * quantities[option.extra_id], 0);

  const handleConfirm = () =>
    onConfirm(
      chosen.map((option) => ({
        extra_id: option.extra_id,
        name: option.name,
        amount: option.amount,
        quantity: quantities[option.extra_id],
      }))
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Extras"
      variant="web-medium"
      renderButtons={() => (
        <>
          <Button variant="tertiary" size="lg" onClick={onClose}>
            Cancel
          </Button>
          {/* "Confirm", not "Add Extras": the modal removes as well as adds, and clearing everything
              then pressing "Add Extras" reads as the opposite of the intent. It matches the flow's
              own vocabulary too — the panels use Confirm for committing a change and Proceed for
              advancing a step, and this commits without advancing. */}
          <Button variant="primary" size="lg" onClick={handleConfirm}>
            Confirm
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-4">
        {/* The list scrolls inside a capped height so an operator with a long extras list still
            leaves the total and the buttons reachable (they sit outside this box). */}
        <div className="-mx-1 max-h-[45vh] overflow-y-auto px-1">
          {options.map((option) => {
            const quantity = quantities[option.extra_id];
            return (
              // A rule under every row, the last one included — it separates the list from the total.
              <div key={option.extra_id} className="flex items-center gap-4 border-b border-neutral-gray py-4">
                {/* Name then price, no description line: an extra has no description to show. The
                    v3 job-data entity carries extra_id / uuid / name / image / cost and nothing
                    else, which is why the design's second line repeats the name — the mock had no
                    real data either. */}
                <div className="min-w-0 flex-1">
                  <Typography variant="action" className="block truncate">
                    {option.name}
                  </Typography>
                  <Typography variant="action" className="mt-1 block text-success">
                    {price(option.amount ?? 0)}
                  </Typography>
                </div>

                {quantity ? (
                  // Stepping below one drops the extra rather than parking it at zero — the row
                  // returns to its "Add" state, which is what the design shows.
                  <QuantityStepper
                    className="flex-shrink-0"
                    value={quantity}
                    max={MAX_QUANTITY}
                    label={option.name}
                    onChange={(next) => setQuantity(option.extra_id, next)}
                  />
                ) : (
                  <Button variant="secondary" size="lg" className="flex-shrink-0" onClick={() => setQuantity(option.extra_id, 1)}>
                    Add
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {itemCount > 0 && (
          <div className="flex items-center rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-4">
            <Typography variant="action">{`${itemCount} item${itemCount === 1 ? "" : "s"}`}</Typography>
            <span className="mx-2 h-1.5 w-1.5 rounded-full bg-neutral-mid-gray" />
            <Typography variant="paragraph">{price(total)}</Typography>
            <button type="button" className="ml-auto" onClick={() => setQuantities({})}>
              <Typography variant="action" className="text-info-dark">
                Clear all
              </Typography>
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};
